import React, { useState, useEffect } from 'react'
import {
  Button,
  Divider,
  message,
  Radio,
  Upload,
  Tag,
  Spin
} from 'antd'
import {
  CheckCircleFilled,
  LoadingOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import actions from '../../store/actions'
import { general } from '../../constants'
import { bytesToSize } from '../../utils'
import './ContentChoiceSetup.scss'
import CourseDocumentSelectorModal from './CourseDocumentSelectorModal'

const ACCEPTED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    '.docx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    '.pptx',
  'text/plain': '.txt'
}

const ContentChoiceStep = ({
  isCourse = true,
  onCancel,
  onSelect,
  handleBack,
  handleFinalSubmit,
  isSubmitting
}) => {
  const { t } = useTranslation()
  const [mode, setMode] = useState('auto')
  const [showDocumentModal, setShowDocumentModal] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])

  // Upload-related states
  const [uploadFileList, setUploadFileList] = useState([])
  const [fileError, setFileError] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [fileUploadStatus, setFileUploadStatus] = useState({})

  const dispatch = useDispatch()
  const library = useSelector(state => state.library)
  const { uploadProgress, uploadSuccess } = library

  useEffect(() => {
    setSelectedFiles([])
    setUploadFileList([])
    setFileError('')
  }, [mode])

  useEffect(() => {
    checkFileError()
  }, [uploadFileList])

  const checkFileError = () => {
    if (mode === 'upload') {
      if (uploadFileList.length === 0) {
        setFileError(t('v5:please_upload_at_least_one_supported_file'))
      } else if (uploadFileList.length > 5) {
        setFileError(t('v5:you_can_upload_a_maximum_of_files'))
      } else {
        setFileError('')
      }
    }
  }

  const handleFileChange = info => {
    let newFileList = [...info.fileList]

    // Filter files to only accept supported file types
    newFileList = newFileList.filter(file => {
      const isAcceptedType = Object.keys(ACCEPTED_FILE_TYPES).includes(
        file.type
      )

      if (!isAcceptedType) {
        const acceptedExtensions = Object.values(ACCEPTED_FILE_TYPES).join(', ')
        message.error(
          `${file.name} ${t('v5:file_not_supported')} ${acceptedExtensions}`
        )
        return false
      }
      return true
    })

    setUploadFileList(newFileList)
  }

  const handleRemoveSelected = fileId => {
    setSelectedFiles(selectedFiles.filter(f => f.id !== fileId))
  }

  const checkedCompletUpload = item => {
    const filterSuccessFiles = _.filter(
      uploadSuccess,
      ele => ele.id === item.id
    )
    return !(uploadSuccess.length && filterSuccessFiles.length)
  }

  const uploadFiles = async files => {
    setUploadedFiles([])
    setFileUploadStatus({})
    const newUploadedFiles = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'uploading'
        }))
        const res = await dispatch(
          actions.library.upload(file.originFileObj, {
            ai_language: 'en-US',
            isAiEnabled: true
          })
        )
        newUploadedFiles.push(res)
        setUploadedFiles(prev => [...prev, res])
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'success'
        }))
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error)
        message.error(`Failed to upload ${file.name}`)
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'error'
        }))
      }
    }
    return newUploadedFiles
  }

  const handleConfirmation = async () => {
    if (mode === 'manual') {
      if (selectedFiles.length === 0) {
        message.warning('Please select at least one document')
        return
      }
      if (handleFinalSubmit) handleFinalSubmit(selectedFiles)
      return
    }

    if (mode === 'upload') {
      if (uploadFileList.length === 0) {
        message.warning('Please upload at least one file')
        return
      }
      if (fileError) {
        message.error(fileError)
        return
      }

      try {
        setIsUploading(true)
        const newFiles = await uploadFiles(uploadFileList)
        setIsUploading(false)
        if (handleFinalSubmit) handleFinalSubmit(newFiles)
      } catch (e) {
        setIsUploading(false)
      }
      return
    }

    if (handleFinalSubmit) handleFinalSubmit([])
  }

  return (
    <div className="content-choice-modal">
      {isCourse && <div className="content-choice-modal__header">
        {t('v4:course_content_source')}
      </div>}
      <div style={{ marginBottom: 28 }}>
        {isCourse && <div className="content-choice-modal__description">
          {t('v4:choose_how_to_source')}
        </div>}
        <Radio.Group
          value={mode}
          onChange={e => setMode(e.target.value)}
          className="content-choice-modal__radio-group"
          onKeyDown={e => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault()
              e.stopPropagation()
              return false
            }
          }}
          onKeyDownCapture={e => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          onKeyUp={e => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          onKeyPress={e => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
        >
          <div
            className={`content-choice-modal__radio-option content-choice-modal__radio-option--auto ${mode === 'auto' ? 'content-choice-modal__radio-option--active' : ''
              }`}
            onClick={() => {
              setMode('auto')
              if (!isCourse) handleFinalSubmit([])
            }}
            onKeyDown={e => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault()
                e.stopPropagation()
              }
            }}
          >
            <Radio value="auto">
              <div className="content-choice-modal__radio-content">
                <div className="content-choice-modal__radio-content-title">
                  {t('v4:automatic_selection')}
                </div>
                <div className="content-choice-modal__radio-content-description">
                  {t('v4:system_will_automatically_select_relavant_documents')}
                </div>
              </div>
            </Radio>
          </div>
          <div
            className={`content-choice-modal__radio-option content-choice-modal__radio-option--manual ${mode === 'manual' ? 'content-choice-modal__radio-option--active' : ''
              }`}
            onClick={() => {
              setMode('manual')
              setShowDocumentModal(true)
            }}
            onKeyDown={e => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault()
                e.stopPropagation()
              }
            }}
          >
            <Radio value="manual">
              <div className="content-choice-modal__radio-content">
                <div className="content-choice-modal__radio-content-title">
                  {t('v4:manual_selection')}
                </div>
                <div className="content-choice-modal__radio-content-description">
                  {t('v4:choose_specific_documents_from_your_library')}
                </div>
              </div>
            </Radio>
          </div>
          <div
            className={`content-choice-modal__radio-option content-choice-modal__radio-option--upload ${mode === 'upload' ? 'content-choice-modal__radio-option--active' : ''
              }`}
            onClick={() => setMode('upload')}
            onKeyDown={e => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault()
                e.stopPropagation()
              }
            }}
          >
            <Radio value="upload">
              <div className="content-choice-modal__radio-content">
                <div className="content-choice-modal__radio-content-title">
                  {t('v4:upload_files')}
                </div>
                <div className="content-choice-modal__radio-content-description">
                  {t('v4:upload_new_documents')}
                </div>
              </div>
            </Radio>
          </div>
        </Radio.Group>
      </div>

      {mode === 'upload' && (
        <div className="content-choice-modal__upload-section">
          <Divider />
          <div>
            <div style={{ marginBottom: 20 }}>
              <Upload
                multiple
                accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                fileList={uploadFileList}
                onChange={handleFileChange}
                beforeUpload={() => false}
              >
                <Button
                  icon="upload"
                  size="large"
                  className="content-choice-modal__upload-button"
                >
                  {t('v5:select_files_to_upload')}
                </Button>
              </Upload>
              {fileError && (
                <div className="content-choice-modal__upload-error">
                  {fileError}
                </div>
              )}
            </div>

            {uploadFileList.length > 0 && (
              <div className="content-choice-modal__upload-preview">
                <div className="content-choice-modal__upload-header">
                  <div className="content-choice-modal__upload-header-info">
                    {isUploading ? <strong><Spin /> {t('general:uploading')}</strong> : <>
                      <CheckCircleFilled style={{ color: Math.max(0, uploadFileList.length - uploadedFiles.length) === 0 ? '#52c41a' : '' }} />
                      {Math.max(0, uploadFileList.length - uploadedFiles.length) === 0 ? (
                        <strong style={{ color: '#52c41a' }}>
                          {t('v4:all_files_uploaded')}
                        </strong>
                      ) : (
                        <strong>
                          {Math.max(0, uploadFileList.length - uploadedFiles.length)} {t('v4:file')}
                          {Math.max(0, uploadFileList.length - uploadedFiles.length) !== 1 ? 's' : ''}{' '}
                          {t('v4:ready_to_upload')}
                        </strong>
                      )}</>}
                  </div>
                  <Button
                    size="small"
                    onClick={() => {
                      setUploadFileList([])
                      setUploadedFiles([])
                    }}
                    disabled={isUploading}
                    className="content-choice-modal__upload-header-clear"
                  >
                    {t('v4:clear_all')}
                  </Button>
                </div>
                <div className="content-choice-modal__upload-list">
                  {uploadFileList.map((file) => (
                    <div
                      key={file.uid}
                      className="content-choice-modal__upload-item"
                    >
                      {file.isUploading ? (
                        <LoadingOutlined className="content-choice-modal__upload-item-icon" />
                      ) : file.isUploaded ? (
                        <CheckCircleFilled className="content-choice-modal__upload-item-icon" />
                      ) : (
                        <FileTextOutlined className="content-choice-modal__upload-item-icon" />
                      )}
                      <div className="content-choice-modal__upload-item-content">
                        <div className="content-choice-modal__upload-item-content-name">
                          {file.name}
                        </div>
                        <div className="content-choice-modal__upload-item-content-size">
                          {(file.size / 1024).toFixed(2)} KB
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {!isCourse && uploadFileList.length > 0 && (
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    {uploadedFiles.length === uploadFileList.length && !isUploading && (
                      <Upload
                        multiple
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                        fileList={uploadFileList}
                        onChange={handleFileChange}
                        beforeUpload={() => false}
                        showUploadList={false}
                      >
                        <Button
                          icon="upload"
                          size="large"
                          className="content-choice-modal__upload-button"
                        >
                          {t('v4:upload_another_file')}
                        </Button>
                      </Upload>
                    )}
                    <Button
                      type="primary"
                      onClick={handleConfirmation}
                      loading={isUploading}
                      disabled={uploadedFiles.length === uploadFileList.length}
                    >
                      {t('v4:upload_and_submit')}
                    </Button>
                  </div>
                )}

                <div className="content-choice-modal__upload-progress">
                  {uploadProgress.length > 0 &&
                    uploadSuccess.length !== uploadProgress.length &&
                    uploadProgress.map((item, ind) =>
                      item.percent < 100 ? (
                        <div key={item.uid} className="upload-modal__item">
                          <div className="upload-modal-item">
                            <div className="upload-modal-item__percent-border">
                              <div
                                className={`upload-modal-item__percent ${item.status === general.CANCELED
                                  ? 'upload-modal-item__percent__cancel'
                                  : ''
                                  }`}
                                style={{ width: item.percent + '%' }}
                              />
                            </div>
                          </div>

                          {item.status !== general.CANCELED && (
                            <div className="upload-modal-item__weight">
                              {checkedCompletUpload(item) && (
                                <span>
                                  {bytesToSize(item.loaded)} of{' '}
                                  {bytesToSize(item.total)}
                                </span>
                              )}
                              {!checkedCompletUpload(item) && (
                                <span className="upload-modal-item__weight__complete">
                                  {uploadedFiles[ind] &&
                                    uploadedFiles[ind].removed
                                    ? 'Removed'
                                    : 'Completed'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : null
                    )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {mode === 'manual' && (
        <div className="content-choice-modal__manual-section">
          <Divider />
          <div>
            {selectedFiles.length > 0 && (
              <div className="content-choice-modal__selected-files">
                <div className="content-choice-modal__selected-files-container">
                  <div className="content-choice-modal__selected-files-header">
                    <div className="content-choice-modal__selected-files-header-info">
                      <CheckCircleFilled />
                      <strong>
                        {selectedFiles.length} {t('v5:select_documents')}
                        {selectedFiles.length > 1 ? 's' : ''}{' '}
                        {t('v5:selected')}
                      </strong>
                    </div>
                    <Button
                      size="small"
                      onClick={() => setSelectedFiles([])}
                      className="content-choice-modal__selected-files-header-clear"
                    >
                      {t('v4:clear_all')}
                    </Button>
                  </div>
                  <div className="content-choice-modal__selected-files-tags">
                    {selectedFiles.map(file => (
                      <Tag
                        key={file.id}
                        closable
                        onClose={() => handleRemoveSelected(file.id)}
                        title={file.title || file.filename || file.name}
                      >
                        <span>
                          {file.title || file.filename || file.name}
                        </span>
                      </Tag>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <Button
                type="primary"
                size="large"
                icon={<FileTextOutlined />}
                onClick={() => setShowDocumentModal(true)}
                className="content-choice-modal__select-documents-button"
              >
                {selectedFiles.length > 0
                  ? t('v5:change_selected_documents')
                  : t('v5:select_documents')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Document Selector Modal */}
      <CourseDocumentSelectorModal
        t={t}
        visible={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        onSelect={(docs) => {
          setSelectedFiles(docs)
          setShowDocumentModal(false)
          if (!isCourse && docs.length > 0) handleFinalSubmit(docs)
        }}
      />

      {isCourse && <div className="content-choice-modal__footer">
        <Divider />
        <div className="content-choice-modal__footer-actions">
          <Button onClick={handleBack} size="large">
            {t('buttons:back')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmation}
            size="large"
            loading={isSubmitting || isUploading}
            disabled={
              isSubmitting ||
              isUploading ||
              (mode === 'manual' && selectedFiles.length === 0) ||
              (mode === 'upload' &&
                (uploadFileList.length === 0 || !!fileError))
            }
          >
            {t('v2:button_submit')}
          </Button>
        </div>
      </div>}
    </div>
  )
}

export default ContentChoiceStep
